import React, { useReducer } from 'react';
import PropTypes from 'prop-types';
import createNetworkingClient from 'utils/networking-client';
import styles from './styles.scss';
import useFeedAnalytics from '../../hooks/useFeedAnalytics';
import {
  pageProps,
  viewingAthleteProps,
  cursorDataProps,
  feedTypeProps,
  indexProps
} from '../../props/feed-prop-types';
import FeedEntityContext from '../../state/FeedEntityContext';
import { init, reducer } from '../../state/feed-entity-reducer';
import { buildInitialState } from '../../state/feed-entity-init-state';

import PromoEntry from '../shared/PromoEntry';
import useFeedIntersectionObserver from '../../hooks/useFeedIntersectionObserver';

const FancyPromo = ({
  viewingAthlete,
  clubId,
  page,
  entity,
  feedType,
  index,
  cursorData,
  rowData,
  athleteProfileId
}) => {
  const { inView, intersectionObserverRef } = useFeedIntersectionObserver({
    index,
    isParentFeedEntry: true
  });

  const { trackAnalytics } = useFeedAnalytics({
    page,
    entity,
    entityId: rowData.trackable_id, // TODO use entityId computed in Feed.js once react feed container goes to prod
    athleteProfileId,
    feedType,
    index,
    viewingAthleteId: viewingAthlete.id,
    clubId,
    inView
  });

  const [entityState] = useReducer(
    reducer,
    buildInitialState({
      trackAnalytics
    }),
    init
  );

  const providerState = {
    entityState
  };

  const logo = (
    <a
      href={rowData.cta_action.resource}
      alt={rowData.cta_text}
      onClick={() => {
        createNetworkingClient({ baseUrl: null, headers: null }).get(
          rowData.click.resource
        );
        entityState.trackAnalytics({
          action: 'click',
          element: 'fancy_promo_image'
        });
      }}
    >
      <img
        data-testid="logo"
        className={styles.logo}
        src={rowData.image}
        alt={rowData.cta_text}
      />
    </a>
  );

  const bodyProps = {
    entity,
    badge: logo,
    backgroundImage: rowData.background_image,
    impression: rowData.impression.resource,
    click: rowData.click.resource,
    headline: rowData.headline,
    description: rowData.description,
    ctaUrl: rowData.cta_action.resource,
    intersectionObserverRef,
    index,
    cursorData,
    viewingAthlete,
    ctaText: rowData.cta_text
  };

  return (
    <FeedEntityContext.Provider value={providerState}>
      <PromoEntry {...bodyProps} />
    </FeedEntityContext.Provider>
  );
};

FancyPromo.propTypes = {
  // feed entity could be Activity, group_activity, Post etc
  entity: PropTypes.oneOf(['FancyPromo', 'SimplePromo']).isRequired,
  viewingAthlete: viewingAthleteProps.isRequired,
  clubId: PropTypes.string.isRequired,
  athleteProfileId: PropTypes.string.isRequired,
  page: pageProps.isRequired,
  cursorData: cursorDataProps.isRequired,
  feedType: feedTypeProps.isRequired,
  // range of number 0...19 representing rows returned by the server per feed load
  index: indexProps.isRequired,
  rowData: PropTypes.shape({
    headline: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    trackable_id: PropTypes.string,
    image: PropTypes.string,
    impression: PropTypes.shape({
      resource: PropTypes.string
    }),
    click: PropTypes.shape({
      resource: PropTypes.string
    }),
    background_image: PropTypes.string,
    cta_text: PropTypes.string,
    cta_action: PropTypes.shape({
      resource: PropTypes.string
    })
  }).isRequired
};

export default FancyPromo;
